// 2026-10-26 · Identificador del dispositivo (cabecera `SessionIdentifier`).
//
// Lo emite el API al iniciar sesión (fila `Devices`) y viaja en CADA petición:
// el lease de mesa lo exige (400 DEVICE_REQUIRED sin él), igual que el fichaje
// por dispositivo, los comandos remotos o el origen de los eventos en tiempo real.
//
// Parte pura (sin axios) para que `utils/axios.js` pueda usarla al cargar sin
// ciclos de imports. La recuperación contra el API vive en `deviceSession.js`.

export const SESSION_IDENTIFIER_KEY = "sessionIdentifier";
export const SESSION_IDENTIFIER_HEADER = "SessionIdentifier";

/**
 * Valor útil o null. `localStorage.setItem(k, null)` guarda la cadena "null"
 * (y un login sin identificador guardaba ""), así que esas cadenas también
 * cuentan como «sin identificador».
 */
export function normalizeSessionIdentifier(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s || s === "null" || s === "undefined") return null;
  return s;
}

function safeStorage(storage) {
  if (storage) return storage;
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Identificador guardado (clave nueva o la antigua con mayúscula), o null. */
export function readStoredSessionIdentifier(storage) {
  const st = safeStorage(storage);
  if (!st) return null;
  try {
    return (
      normalizeSessionIdentifier(st.getItem(SESSION_IDENTIFIER_KEY)) ||
      normalizeSessionIdentifier(st.getItem("SessionIdentifier"))
    );
  } catch {
    return null;
  }
}

const changeListeners = new Set();
/** Aviso cuando cambia el identificador guardado (p. ej. para re-suscribir hubs). */
export function onSessionIdentifierChange(fn) {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
}

/**
 * Persiste y pone en la cabecera común el identificador. Con un valor vacío
 * ("", "null", undefined…) borra ambos: nunca se manda una cabecera vacía ni
 * se guarda la cadena "null".
 */
export function applySessionIdentifier(value, { storage, headers } = {}) {
  const sid = normalizeSessionIdentifier(value);
  const st = safeStorage(storage);
  const before = readStoredSessionIdentifier(st);
  try {
    if (st) {
      if (sid) st.setItem(SESSION_IDENTIFIER_KEY, sid);
      else st.removeItem(SESSION_IDENTIFIER_KEY);
    }
  } catch {
    /* modo privado: la cabecera basta para esta carga */
  }
  if (headers) {
    if (sid) headers[SESSION_IDENTIFIER_HEADER] = sid;
    else delete headers[SESSION_IDENTIFIER_HEADER];
  }
  if (before !== sid) {
    changeListeners.forEach((fn) => {
      try {
        fn(sid);
      } catch {
        /* ignore */
      }
    });
  }
  return sid;
}

/** Identificador efectivo: el de la cabecera o, si se perdió, el guardado. */
export function currentSessionIdentifier({ storage, headers } = {}) {
  return normalizeSessionIdentifier(headers?.[SESSION_IDENTIFIER_HEADER]) || readStoredSessionIdentifier(storage);
}

/** ¿La respuesta es el 400 del lease por falta de dispositivo? */
export function isDeviceRequiredError(err) {
  const status = err?.status ?? err?.response?.status;
  const body = err?.response?.data && typeof err.response.data === "object" ? err.response.data : err;
  const code = body?.code || body?.errorCode || err?.errorCode || null;
  return code === "DEVICE_REQUIRED" && (status == null || status === 400);
}
